/**
 * Anchor Bullion Gold Price Page JavaScript
 * Handles live price updates, calculators, and UI interactions
 */

(function () {
  'use strict';

  // =========================
  // Constants
  // =========================
  const TROY_OZ_TO_GRAM = 31.1034768;
  const TROY_OZ_TO_KILO = 0.0311034768;

  // TESTING MODE: Set to false for production
  const TESTING_MODE = false;
  const REFRESH_INTERVAL = TESTING_MODE ? 3600000 : 300000; // 1 hour for both

  const STORAGE_KEY = 'ab_gold_currency';
  const PRICE_CACHE_KEY = 'ab_gold_price_cache';
  const CACHE_DURATION = TESTING_MODE ? 3600000 : 300000; // 1 hour cache for both

  // =========================
  // State
  // =========================
  let currentPrices = {
    bid: 2395.12,
    ask: 2396.02,
    mid: 2395.57,
    change: -5.21,
    changePct: -0.22,
    prevClose: 2400.78,
  };

  let currentFX = {
    EURUSD: 1.085,
    GBPUSD: 1.285,
    USDJPY: 154.1,
    USDCAD: 1.34,
    AUDUSD: 0.66,
  };

  let selectedCurrency = 'USD';
  let apiUrl = '';
  let refreshTimer = null;
  let retryCount = 0;
  let retryDelay = 1000;

  // =========================
  // DOM helpers
  // =========================
  function getDom() {
    const card = document.querySelector('.ab-price-card');
    return {
      // Card sections
      card,
      loading: card?.querySelector('.ab-price-loading'),
      content: card?.querySelector('.ab-price-content'),
      error: card?.querySelector('.ab-price-error'),

      // Main prices in the card
      priceBid: document.querySelector('[data-price="bid"]'),
      priceMid: document.querySelector('[data-price="mid"]'),
      priceAsk: document.querySelector('[data-price="ask"]'),

      // Unit tiles in the card
      unitOz: document.querySelector('[data-unit="oz"], [data-unit="ounce"], [data-unit="per-ounce"]'),
      unitGram: document.querySelector('[data-unit="gram"]'),
      unitKilo: document.querySelector('[data-unit="kilo"], [data-unit="kg"]'),

      // Change elements in the card
      changeAmt: document.querySelector('[data-change="amount"]'),
      changePct: document.querySelector('[data-change="percent"]'),

      // Timestamp area
      lastUpdatedTime: document.querySelector('.ab-last-updated time, time[data-timestamp]'),

      // Currency UI
      currencySelect: document.querySelector('.ab-currency-select'),
      currencyTabs: document.querySelectorAll('.ab-currency-tabs [data-currency]'),

      // Table body (either explicit ID or the first table body)
      priceTableBody: document.getElementById('ab-price-tbody') || document.querySelector('.ab-price-table tbody'),

      // Sticky pill
      stickyPill: document.querySelector('.ab-sticky-pill'),
      stickyPrice: document.querySelector('[data-sticky-price]'),
      stickyChange: document.querySelector('[data-sticky-change]'),

      // Calculators outputs
      calcOut: {
        portfolioBid: document.querySelector('[data-calc="portfolio-bid"]'),
        portfolioAsk: document.querySelector('[data-calc="portfolio-ask"]'),
        fineGold: document.querySelector('[data-calc="fine-gold"]'),
        jewelryValue: document.querySelector('[data-calc="jewelry-value"]'),
        finalPrice: document.querySelector('[data-calc="final-price"]'),
        premiumAmount: document.querySelector('[data-calc="premium-amount"]'),
      },
    };
  }

  // Toggle UI states: "loading" | "ready" | "error"
  function togglePriceUI(state) {
    const d = getDom();
    if (!d.card) return;
    if (d.loading) d.loading.style.display = state === 'loading' ? '' : 'none';
    if (d.content) d.content.style.display = state === 'ready' ? '' : 'none';
    if (d.error) d.error.style.display = state === 'error' ? '' : 'none';
  }

  // =========================
  // Init
  // =========================
  function init() {
    // Start as loading
    togglePriceUI('loading');

    // Get API URL from data attribute
    const section = document.querySelector('.ab-gold-price-section');
    if (section) {
      apiUrl = section.dataset.apiUrl || window.AB_GOLD_API_URL || '';
    }

    // Load saved currency preference
    const savedCurrency = localStorage.getItem(STORAGE_KEY);
    if (savedCurrency && ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'].includes(savedCurrency)) {
      selectedCurrency = savedCurrency;
    }

    // Try to load cached prices first
    const hadCache = loadCachedPrices();

    // Set up event listeners
    setupEventListeners();

    // Initialize UI with whatever we have
    updateCurrencySelector();
    updateAllPrices();

    // Set up calculators & sticky pill
    setupCalculators();
    setupStickyPill();

    // Start price updates if API URL is available
    if (apiUrl) {
      const shouldFetch = shouldFetchNewPrices();

      if (shouldFetch) {
        fetchPrices();
      } else {
        // Cached is fresh; show immediately
        togglePriceUI('ready');
        updateLastUpdatedTime();
      }

      startPriceRefresh();
    } else {
      console.warn('No API URL configured for gold price updates');
      showApiError();
    }

    // Show testing mode indicator
    if (TESTING_MODE) {
      showTestingModeIndicator();
    }
  }

  // =========================
  // Cache helpers
  // =========================
  function shouldFetchNewPrices() {
    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    if (!cache) return true;
    try {
      const cacheData = JSON.parse(cache);
      const cacheAge = Date.now() - cacheData.timestamp;
      return cacheAge > CACHE_DURATION;
    } catch {
      return true;
    }
  }

  function getTimeUntilNextFetch() {
    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    if (!cache) return 'now';
    try {
      const cacheData = JSON.parse(cache);
      const cacheAge = Date.now() - cacheData.timestamp;
      const timeUntilFetch = CACHE_DURATION - cacheAge;
      if (timeUntilFetch <= 0) return 'now';
      const minutes = Math.floor(timeUntilFetch / 60000);
      const seconds = Math.floor((timeUntilFetch % 60000) / 1000);
      return minutes > 0
        ? `${minutes} minute${minutes !== 1 ? 's' : ''} ${seconds} second${seconds !== 1 ? 's' : ''}`
        : `${seconds} second${seconds !== 1 ? 's' : ''}`;
    } catch {
      return 'now';
    }
  }

  function loadCachedPrices() {
    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    if (!cache) return false;
    try {
      const cacheData = JSON.parse(cache);
      const cacheAge = Date.now() - cacheData.timestamp;
      if (cacheAge > CACHE_DURATION) {
        console.log('[Cache] Expired, will fetch new prices');
        return false;
      }
      if (cacheData.prices) currentPrices = { ...currentPrices, ...cacheData.prices };
      if (cacheData.fx) currentFX = { ...currentFX, ...cacheData.fx };
      console.log('[Cache] Loaded prices from cache, age:', Math.floor(cacheAge / 1000), 'seconds');
      return true;
    } catch (e) {
      console.error('[Cache] Error loading cached prices:', e);
      return false;
    }
  }

  function savePricesToCache() {
    const cacheData = {
      timestamp: Date.now(),
      prices: currentPrices,
      fx: currentFX,
    };
    try {
      localStorage.setItem(PRICE_CACHE_KEY, JSON.stringify(cacheData));
      console.log('[Cache] Prices saved to cache');
    } catch (e) {
      console.error('[Cache] Error saving prices:', e);
    }
  }

  // =========================
  // Testing UI
  // =========================
  function showTestingModeIndicator() {
    const indicator = document.createElement('div');
    indicator.id = 'ab-testing-indicator';
    indicator.style.cssText = `
      position: fixed;
      top: 10px;
      right: 10px;
      background: #fbbf24;
      color: #78350f;
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10000;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;
    indicator.innerHTML = `
      <span>🧪 Testing Mode: API calls limited to once/hour</span>
      <button id="ab-force-refresh" style="margin-left: 10px; padding: 2px 8px; background: #78350f; color: white; border: none; border-radius: 3px; cursor: pointer;">Force Refresh</button>
    `;
    document.body.appendChild(indicator);

    document.getElementById('ab-force-refresh').addEventListener('click', () => {
      console.log('[Testing] Force refresh triggered');
      localStorage.removeItem(PRICE_CACHE_KEY);
      fetchPrices();
    });

    setInterval(() => {
      const timeUntil = getTimeUntilNextFetch();
      const button = document.getElementById('ab-force-refresh');
      if (button) button.title = `Next automatic update in: ${timeUntil}`;
    }, 1000);
  }

  // =========================
  // Event listeners
  // =========================
  function setupEventListeners() {
    // Price alert (if present)
    const alertTrigger = document.getElementById('ab-alert-trigger');
    const alertContainer = document.getElementById('ab-alert-container');
    const alertForm = document.getElementById('ab-alert-form');
    const alertCancel = document.getElementById('ab-alert-cancel');
    if (alertTrigger && alertContainer) {
      alertTrigger.addEventListener('click', () => {
        alertContainer.style.display = alertContainer.style.display === 'none' ? 'block' : 'none';
      });
    }
    if (alertCancel && alertContainer) {
      alertCancel.addEventListener('click', () => {
        alertContainer.style.display = 'none';
      });
    }
    if (alertForm) {
      alertForm.addEventListener('submit', handleAlertSubmit);
    }

    // Currency select (class, not ID)
    const d = getDom();
    if (d.currencySelect) {
      d.currencySelect.addEventListener('change', handleCurrencyChange);
    }

    // Currency tabs (buttons with data-currency)
    d.currencyTabs.forEach((btn) => {
      btn.addEventListener('click', () => {
        selectedCurrency = btn.dataset.currency;
        localStorage.setItem(STORAGE_KEY, selectedCurrency);
        setActiveCurrencyTab(selectedCurrency);
        updatePriceTable();
      });
    });

    // Chart period tabs
    document.querySelectorAll('.ab-chart-periods [role="tab"]').forEach((tab) => {
      tab.addEventListener('click', () => {
        document.querySelectorAll('.ab-chart-periods [role="tab"]').forEach((t) => t.setAttribute('aria-selected', 'false'));
        tab.setAttribute('aria-selected', 'true');
        console.log('Chart range selected:', tab.dataset.period);
      });
    });
  }

  // =========================
  // Fetch prices
  // =========================
 async function fetchPrices() {
  if (!apiUrl) return;

  if (TESTING_MODE && !shouldFetchNewPrices()) {
    console.log('[Testing Mode] Skipping API call - using cached data');
    togglePriceUI('ready');
    return;
  }

  const startTime = Date.now();
  console.log('[API] Fetching prices from:', apiUrl);

  try {
    const response = await fetch(apiUrl, { 
      headers: { 'Accept': 'application/json' } 
    });
    
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    
    const data = await response.json();
    console.log('[API] Response received in', Date.now() - startTime, 'ms');
    console.log('[API] Data structure:', data); // Debug log

    // Parse the response - FIXED to match Pipedream structure
    if (data.metals && data.metals.gold) {
      const gold = data.metals.gold;
      
      currentPrices.bid = typeof gold.bid === 'number' ? gold.bid : currentPrices.bid;
      currentPrices.ask = typeof gold.ask === 'number' ? gold.ask : currentPrices.ask;
      currentPrices.mid = typeof gold.mid === 'number' ? gold.mid : currentPrices.mid;
      currentPrices.change = typeof gold.change === 'number' ? gold.change : currentPrices.change;
      currentPrices.changePct = typeof gold.changePct === 'number' ? gold.changePct : currentPrices.changePct;
      
      if (typeof gold.prevCloseUSD === 'number') {
        currentPrices.prevClose = gold.prevCloseUSD;
      }
    }

    // Update FX rates if provided
    if (data.fx && typeof data.fx === 'object') {
      Object.assign(currentFX, data.fx);
    }

    // Save to cache
    savePricesToCache();

    // Update UI and show content
    updateAllPrices();
    updateLastUpdatedTime();
    togglePriceUI('ready');

    // Reset retry counter on success
    retryCount = 0;
    retryDelay = 1000;

    if (TESTING_MODE) {
      showNotification('✅ Prices updated from API', 'success');
    }
  } catch (error) {
    console.error('[API] Error fetching gold prices:', error);
    handleFetchError();
  }
}

  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
      background: ${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#3b82f6'};
      color: white;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10001;
      animation: slideIn 0.3s ease;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  function handleFetchError() {
    retryCount++;
    const maxRetries = TESTING_MODE ? 2 : 5;

    if (retryCount <= maxRetries) {
      retryDelay = Math.min(retryDelay * 2, TESTING_MODE ? 60000 : 30000);
      console.log(`[API] Retrying in ${retryDelay / 1000} seconds... (attempt ${retryCount}/${maxRetries})`);
      if (TESTING_MODE) showNotification(`⚠️ API error. Retry in ${retryDelay / 1000}s`, 'error');
      setTimeout(fetchPrices, retryDelay);
    } else {
      showApiError();
      if (TESTING_MODE) showNotification('❌ API unavailable. Using cached/default values', 'error');
    }
  }

  function showApiError() {
    togglePriceUI('error');
  }

  // =========================
  // Refresh scheduler
  // =========================
 function startPriceRefresh() {
    if (refreshTimer) clearInterval(refreshTimer);
    refreshTimer = setInterval(fetchPrices, REFRESH_INTERVAL);
  }

  // =========================
  // Rendering
  // =========================
  function updateAllPrices() {
    const d = getDom();
    if (!d) return;

    // Main price card values
    if (d.priceBid) d.priceBid.textContent = formatPrice(currentPrices.bid);
    if (d.priceMid) d.priceMid.textContent = formatPrice(currentPrices.mid);
    if (d.priceAsk) d.priceAsk.textContent = formatPrice(currentPrices.ask);

    // Per-unit tiles in card
    if (d.unitOz) d.unitOz.textContent = formatPrice(currentPrices.mid);
    if (d.unitGram) d.unitGram.textContent = formatPrice(currentPrices.mid / TROY_OZ_TO_GRAM);
    if (d.unitKilo) d.unitKilo.textContent = formatPrice(currentPrices.mid / TROY_OZ_TO_KILO);

    // Change area
    if (d.changeAmt) {
      const signAmt = currentPrices.change >= 0 ? '+' : '';
      d.changeAmt.textContent = `${signAmt}${formatPrice(currentPrices.change)}`;
      d.changeAmt.classList.toggle('positive', currentPrices.change >= 0);
      d.changeAmt.classList.toggle('negative', currentPrices.change < 0);
    }
    if (d.changePct) {
      const signPct = currentPrices.changePct >= 0 ? '+' : '';
      d.changePct.textContent = `(${signPct}${currentPrices.changePct.toFixed(2)}%)`;
    }

    // Table, sticky pill, timestamp
    updatePriceTable();
    updateStickyPill();
    updateLastUpdatedTime();

    // Keep the premium calculator's spot price in sync (uses id="spot-price" in markup)
    const spotInput = document.getElementById('spot-price');
    if (spotInput) {
      spotInput.value = currentPrices.mid.toFixed(2);
    }
  }

  function updatePriceTable() {
    const d = getDom();
    if (!d.priceTableBody) return;

    const rate = getCurrencyRate(selectedCurrency);
    const units = [
      { name: 'Troy Ounce', factor: 1, unit: 'oz' },
      { name: 'Gram', factor: 1 / TROY_OZ_TO_GRAM, unit: 'g' },
      { name: '10 Grams', factor: 10 / TROY_OZ_TO_GRAM, unit: '10g' },
      { name: 'Pennyweight (dwt)', factor: 0.05, unit: 'dwt' },
      { name: 'Tola', factor: 0.375, unit: 'tola' },
      { name: 'Tael (Hong Kong)', factor: 1.20337, unit: 'tael' },
      { name: 'Kilogram', factor: 1 / TROY_OZ_TO_KILO, unit: 'kg' },
    ];

    let html = '';
    units.forEach((u) => {
      const bid = currentPrices.bid * u.factor * rate;
      const ask = currentPrices.ask * u.factor * rate;
      const mid = currentPrices.mid * u.factor * rate;
      const change = currentPrices.change * u.factor * rate;

      html += `
        <tr data-unit="${u.unit}">
          <td>${u.name}</td>
          <td class="ab-bid">${formatCurrencyPrice(bid, selectedCurrency)}</td>
          <td class="ab-ask">${formatCurrencyPrice(ask, selectedCurrency)}</td>
          <td class="ab-mid">${formatCurrencyPrice(mid, selectedCurrency)}</td>
          <td class="ab-change">${formatCurrencyPrice(change, selectedCurrency, true)}</td>
          <td class="ab-percent ${currentPrices.changePct >= 0 ? 'positive' : 'negative'}">
            ${currentPrices.changePct >= 0 ? '+' : ''}${currentPrices.changePct.toFixed(2)}%
          </td>
        </tr>
      `;
    });

    d.priceTableBody.innerHTML = html;
  }

  function getCurrencyRate(currency) {
    switch (currency) {
      case 'EUR':
        return 1 / currentFX.EURUSD;
      case 'GBP':
        return 1 / currentFX.GBPUSD;
      case 'JPY':
        return currentFX.USDJPY;
      case 'CAD':
        return currentFX.USDCAD;
      case 'AUD':
        return 1 / currentFX.AUDUSD;
      default:
        return 1; // USD
    }
  }

  function updateLastUpdatedTime() {
    const d = getDom();
    if (!d.lastUpdatedTime) return;

    const cache = localStorage.getItem(PRICE_CACHE_KEY);
    let timestamp = Date.now();
    if (cache) {
      try {
        const cacheData = JSON.parse(cache);
        timestamp = cacheData.timestamp || timestamp;
      } catch {}
    }

    const date = new Date(timestamp);
    const timeStr =
      date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York',
      }) + ' ET';

    d.lastUpdatedTime.textContent = timeStr;
    d.lastUpdatedTime.setAttribute('datetime', date.toISOString());

    if (TESTING_MODE) {
      const cacheAge = Date.now() - timestamp;
      const ageMinutes = Math.floor(cacheAge / 60000);
      if (ageMinutes > 0) d.lastUpdatedTime.textContent += ` (${ageMinutes}m ago)`;
    }
  }

  // =========================
  // Currency controls
  // =========================
  function handleCurrencyChange(event) {
    selectedCurrency = event.target.value;
    localStorage.setItem(STORAGE_KEY, selectedCurrency);
    setActiveCurrencyTab(selectedCurrency);
    updatePriceTable();
  }

  function updateCurrencySelector() {
    const d = getDom();
    if (d.currencySelect) d.currencySelect.value = selectedCurrency;
    setActiveCurrencyTab(selectedCurrency);
  }

  function setActiveCurrencyTab(currency) {
    document.querySelectorAll('.ab-currency-tabs [data-currency]').forEach((btn) => {
      btn.setAttribute('aria-selected', btn.dataset.currency === currency ? 'true' : 'false');
    });
    const select = document.querySelector('.ab-currency-select');
    if (select) select.value = currency;
  }

  // =========================
  // Price alerts (demo)
  // =========================
  function handleAlertSubmit(event) {
    event.preventDefault();
    const formData = new FormData(event.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      direction: formData.get('direction'),
      price: formData.get('price'),
    };
    console.log('Price alert request:', data);
    const successDiv = document.getElementById('ab-alert-success');
    if (successDiv) {
      successDiv.style.display = 'block';
      setTimeout(() => {
        successDiv.style.display = 'none';
        const container = document.getElementById('ab-alert-container');
        if (container) container.style.display = 'none';
        event.target.reset();
      }, 3000);
    }
  }

  // =========================
  // Calculators (match markup IDs + data-calc outputs)
  // =========================
  // =========================
// Calculators (robust bindings + first-run compute)
// =========================
function setupCalculators() {
  const TROY_OZ_TO_GRAM = 31.1034768;
  const TROY_OZ_TO_KILO = 0.0311034768;

  // Helpers (local)
  const $id = (x) => document.getElementById(x);
  const $ = (sel, root = document) => root.querySelector(sel);

  // ----- Portfolio Value -----
  // Try multiple possible IDs used across pages/themes
  const portfolioAmount =
    $id('portfolio-amount') ||
    $id('ab-portfolio-amount') ||
    $('[data-input="portfolio-amount"]');

  const portfolioUnit =
    $id('portfolio-unit') ||
    $id('ab-portfolio-unit') ||
    $('[data-input="portfolio-unit"]');

  // Outputs (support alt data-calc names)
  const outPortfolioBid =
    $('[data-calc="portfolio-bid"]') || $id('ab-portfolio-bid');
  const outPortfolioAsk =
    $('[data-calc="portfolio-ask"]') || $id('ab-portfolio-ask');

  const updatePortfolio = () => {
    if (!portfolioAmount || !portfolioUnit) return;
    const amount = parseFloat(portfolioAmount.value) || 0;
    const unit = (portfolioUnit.value || '').toLowerCase();

    // Normalize to troy ounces
    let oz = amount;
    if (unit === 'g' || unit === 'gram' || unit === 'grams') oz = amount / TROY_OZ_TO_GRAM;
    if (unit === 'kg' || unit === 'kilo' || unit === 'kilogram') oz = amount / TROY_OZ_TO_KILO;

    const bidValue = oz * (Number(currentPrices.bid) || 0);
    const askValue = oz * (Number(currentPrices.ask) || 0);

    if (outPortfolioBid) outPortfolioBid.textContent = formatPrice(bidValue);
    if (outPortfolioAsk) outPortfolioAsk.textContent = formatPrice(askValue);
  };

  if (portfolioAmount && portfolioUnit) {
    portfolioAmount.addEventListener('input', updatePortfolio);
    portfolioUnit.addEventListener('change', updatePortfolio);
  }

  // ----- Jewelry / Scrap Gold -----
  const jewelryWeight =
    $id('jewelry-weight') ||
    $id('ab-jewelry-weight') ||
    $('[data-input="jewelry-weight"]');

  const jewelryUnit =
    $id('jewelry-unit') ||
    $id('ab-jewelry-unit') ||
    $('[data-input="jewelry-unit"]');

  // karat selector (expects numeric like 24, 22, 18, etc.)
  const jewelryKarat =
    $id('jewelry-karat') ||
    $id('ab-jewelry-karat') ||
    $('[data-input="jewelry-karat"]');

  // Outputs (support both fine-gold / fine-metal and jewelry-value / scrap-value)
  const outFine =
    $('[data-calc="fine-gold"]') ||
    $('[data-calc="fine-metal"]') ||
    $id('ab-scrap-fine');

  const outJewelryValue =
    $('[data-calc="jewelry-value"]') ||
    $('[data-calc="scrap-value"]') ||
    $id('ab-scrap-value');

  const updateJewelry = () => {
    if (!jewelryWeight || !jewelryKarat) return;
    const weight = parseFloat(jewelryWeight.value) || 0;
    const unit = (jewelryUnit?.value || 'gram').toLowerCase();
    const karat = parseFloat(jewelryKarat.value) || 24;
    const purity = Math.max(0, Math.min(1, karat / 24)); // 0..1

    // convert to grams
    const grams = unit === 'oz' ? weight * TROY_OZ_TO_GRAM : weight;
    const fineGrams = grams * purity;
    const ozFine = fineGrams / TROY_OZ_TO_GRAM;
    const value = ozFine * (Number(currentPrices.mid) || 0);

    if (outFine) outFine.textContent = `${fineGrams.toFixed(2)} g`;
    if (outJewelryValue) outJewelryValue.textContent = formatPrice(value);
  };

  if (jewelryWeight) {
    jewelryWeight.addEventListener('input', updateJewelry);
    jewelryUnit?.addEventListener('change', updateJewelry);
    jewelryKarat?.addEventListener('change', updateJewelry);
  }

  // ----- Premium Calculator -----
  const premiumPercent =
    $id('premium-percent') ||
    $id('ab-premium-percent') ||
    $('[data-input="premium-percent"]');

  const spotInput =
    $id('spot-price') ||
    $id('ab-premium-spot') ||
    $('[data-input="spot-price"]');

  const outPremiumAmount =
    $('[data-calc="premium-amount"]') || $id('ab-premium-amount');

  const outFinalPrice =
    $('[data-calc="final-price"]') || $id('ab-premium-final');

  const updatePremium = () => {
    if (!premiumPercent) return;
    const premium = parseFloat(premiumPercent.value) || 0;
    const spot = spotInput ? (parseFloat(spotInput.value) || Number(currentPrices.mid) || 0) : (Number(currentPrices.mid) || 0);
    const premAmt = spot * (premium / 100);
    const final = spot + premAmt;

    if (outPremiumAmount) outPremiumAmount.textContent = formatPrice(premAmt);
    if (outFinalPrice) outFinalPrice.textContent = formatPrice(final);
  };

  if (premiumPercent) {
    premiumPercent.addEventListener('input', updatePremium);
    spotInput?.addEventListener('input', updatePremium);
  }

  // ----- First-run compute so numbers change immediately -----
  updatePortfolio();
  updateJewelry();
  updatePremium();
}

  // =========================
  // Sticky price pill
  // =========================
  function setupStickyPill() {
    const d = getDom();
    const pill = d.stickyPill;
    if (!pill) return;

    let ticking = false;

    function updatePillVisibility() {
      const scrollPos = window.scrollY;
      const priceCard = document.querySelector('.ab-price-card');
      if (priceCard) {
        const cardBottom = priceCard.offsetTop + priceCard.offsetHeight;
        if (scrollPos > cardBottom) {
          pill.classList.add('active');
          // If inline style="display:none" exists in markup, remove it
          if (pill.style.display === 'none') pill.style.display = '';
        } else {
          pill.classList.remove('active');
        }
      }
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(updatePillVisibility);
        ticking = true;
      }
    }

    if (window.innerWidth <= 768) {
      window.addEventListener('scroll', requestTick);
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) {
        pill.classList.remove('active');
      }
    });
  }

  function updateStickyPill() {
    const d = getDom();
    if (d.stickyPrice) d.stickyPrice.textContent = formatPrice(currentPrices.mid);
    if (d.stickyChange) {
      const sign = currentPrices.changePct >= 0 ? '+' : '';
      d.stickyChange.textContent = `${sign}${currentPrices.changePct.toFixed(2)}%`;
      d.stickyChange.classList.toggle('positive', currentPrices.changePct >= 0);
      d.stickyChange.classList.toggle('negative', currentPrices.changePct < 0);
    }
  }

  // =========================
  // Utilities
  // =========================
  function formatPrice(value) {
    return '$' + Number(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function formatCurrencyPrice(value, currency, showSign = false) {
    const symbols = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CAD: 'C$',
      AUD: 'A$',
    };
    const symbol = symbols[currency] || '$';
    const decimals = currency === 'JPY' ? 0 : 2;
    const abs = Math.abs(value).toFixed(decimals).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const sign = showSign && value !== 0 ? (value > 0 ? '+' : '-') : '';
    return sign + symbol + abs;
  }

  function updateElement(id, text) {
    const element = document.getElementById(id);
    if (element) element.textContent = text;
  }

  // =========================
  // Animations (testing)
  // =========================
  if (TESTING_MODE) {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      @keyframes slideOut { from { transform: translateX(0); opacity: 1; } to { transform: translateX(100%); opacity: 0; } }
    `;
    document.head.appendChild(style);
  }

  // =========================
  // Boot
  // =========================
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
